// src/pages/AuditDetailPage.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Card, Tag, Table, Select, Upload, Button, Input, message } from 'antd';
import { UploadOutlined, ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';
import { mockAudits, mockClients } from '../data/mockData';
import { AuditItem } from '../types';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AuditDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.currentUser);
  
  const audit = mockAudits.find(a => a.id === id);
  const [items, setItems] = useState<AuditItem[]>(audit?.items || []);

  if (!audit) {
    return (
      <div>
        <Title level={2}>Auditoría no encontrada</Title>
        <Button onClick={() => navigate('/dashboard/audits')}>Volver a Auditorías</Button>
      </div>
    );
  }

  const client = mockClients.find(c => c.id === audit.clientId);

  const getStatusTag = (status: AuditItem['status']) => {
    const statusConfig = {
      'pending': { color: 'default', text: 'Pendiente' },
      'compliant': { color: 'success', text: 'Conforme' },
      'non-compliant': { color: 'error', text: 'No Conforme' },
      'n/a': { color: 'default', text: 'N/A' },
    };
    const config = statusConfig[status];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleStatusChange = (itemId: string, newStatus: AuditItem['status']) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, status: newStatus } : item
    ));
    message.success('Estado actualizado');
  };

  const handleNotesChange = (itemId: string, notes: string) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, adminNotes: notes } : item
    ));
  };

  const handleEvidenceUpload = (itemId: string, file: UploadFile) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, evidenceFile: file.name } : item
    ));
    message.success('Evidencia subida exitosamente');
  };

  const columns: ColumnsType<AuditItem> = [
    {
      title: 'Ítem de Verificación',
      dataIndex: 'text',
      key: 'text',
      width: '30%',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status: AuditItem['status'], record: AuditItem) => {
        if (currentUser?.role === 'admin') {
          return (
            <Select
              value={status}
              style={{ width: '100%' }}
              onChange={(value) => handleStatusChange(record.id, value)}
            >
              <Select.Option value="pending">Pendiente</Select.Option>
              <Select.Option value="compliant">Conforme</Select.Option>
              <Select.Option value="non-compliant">No Conforme</Select.Option>
              <Select.Option value="n/a">N/A</Select.Option>
            </Select>
          );
        }
        return getStatusTag(status);
      },
    },
    {
      title: 'Evidencia',
      dataIndex: 'evidenceFile',
      key: 'evidenceFile',
      width: '20%',
      render: (evidenceFile: string | undefined, record: AuditItem) => {
        if (currentUser?.role === 'client') {
          // Cliente puede subir evidencia si está pendiente o no conforme
          if (record.status === 'pending' || record.status === 'non-compliant') {
            return (
              <Upload
                beforeUpload={(file) => {
                  handleEvidenceUpload(record.id, file as UploadFile);
                  return false;
                }}
                maxCount={1}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} size="small">
                  {evidenceFile || 'Subir'}
                </Button>
              </Upload>
            );
          }
          return evidenceFile ? <Text>{evidenceFile}</Text> : <Text type="secondary">-</Text>;
        } else {
          // Admin solo ve el archivo
          return evidenceFile ? (
            <Button 
              type="link" 
              icon={<LinkOutlined />} 
              size="small"
              onClick={() => message.info(`Descargar: ${evidenceFile}`)}
            >
              {evidenceFile}
            </Button>
          ) : (
            <Text type="secondary">Sin evidencia</Text>
          );
        }
      },
    },
    {
      title: 'Notas del Admin',
      dataIndex: 'adminNotes',
      key: 'adminNotes',
      width: '35%',
      render: (notes: string, record: AuditItem) => {
        if (currentUser?.role === 'admin') {
          return (
            <TextArea
              value={notes}
              onChange={(e) => handleNotesChange(record.id, e.target.value)}
              placeholder="Agregar notas..."
              autoSize={{ minRows: 1, maxRows: 3 }}
            />
          );
        }
        return notes ? <Text>{notes}</Text> : <Text type="secondary">-</Text>;
      },
    },
  ];

  return (
    <div>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate('/dashboard/audits')}
        style={{ marginBottom: 16 }}
      >
        Volver a Auditorías
      </Button>

      <Card style={{ marginBottom: 24 }}>
        <Title level={2}>{audit.title}</Title>
        <div style={{ marginTop: 16 }}>
          <Text strong>Cliente: </Text>
          <Text>{client?.companyName}</Text>
        </div>
        <div style={{ marginTop: 8 }}>
          <Text strong>Estado: </Text>
          {audit.status === 'pending' && <Tag color="default">Pendiente</Tag>}
          {audit.status === 'in-progress' && <Tag color="processing">En Progreso</Tag>}
          {audit.status === 'completed' && <Tag color="success">Completada</Tag>}
        </div>
      </Card>

      <Card title="Checklist de Auditoría">
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default AuditDetailPage;
